import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Zap, ClipboardEdit, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { api } from "../lib/config";

/**
 * Config-driven instant-quote wizard. One component renders the correct
 * fields for each service, POSTs to /api/instant-quote/{service}, then
 * either shows the price + Book Now OR falls back to /quote/request when
 * the pricer indicates `needs_manual_quote`.
 */

const FIELDS = {
  bush_trimming: {
    title: "Bush Trimming",
    fields: [
      { key: "bush_count",   label: "Number of bushes", type: "number", min: 1, max: 30, initial: 5 },
      { key: "size",         label: "Average size",     type: "select", options: [["small","Small"],["medium","Medium"],["large","Large"]], initial: "medium" },
      { key: "height_ft",    label: "Height (ft)",      type: "number", min: 1, max: 20, initial: 4, step: 0.5 },
      { key: "remove_debris",label: "Remove debris?",   type: "bool", initial: true },
    ],
  },
  mulching: {
    title: "Mulching",
    fields: [
      { key: "bed_sqft",              label: "Flower bed size (sq ft)", type: "number", min: 20, max: 2000, initial: 200 },
      { key: "depth_inches",          label: "Mulch depth (inches)",   type: "number", min: 1, max: 6, initial: 3 },
      { key: "customer_supplies_mulch",label: "You supply mulch?",     type: "bool", initial: false },
    ],
  },
  gutter_cleaning: {
    title: "Gutter Cleaning",
    fields: [
      { key: "linear_feet", label: "Linear feet of gutters", type: "number", min: 20, max: 400, initial: 120 },
      { key: "two_story",   label: "Two-story home?",         type: "bool", initial: false },
      { key: "heavy_debris",label: "Heavy debris?",           type: "bool", initial: false },
    ],
  },
  pressure_washing: {
    title: "Pressure Washing",
    fields: [
      { key: "area_sqft",   label: "Area to clean (sq ft)", type: "number", min: 100, max: 5000, initial: 500 },
      { key: "surface",     label: "Surface",               type: "select", options: [["concrete","Concrete"],["brick","Brick"],["paver","Paver"],["wood","Wood/Deck"]], initial: "concrete" },
      { key: "oil_stains",  label: "Includes oil stains?",  type: "bool", initial: false },
    ],
  },
  junk_removal: {
    title: "Junk Removal",
    fields: [
      { key: "truck_portion", label: "How much stuff?", type: "select",
        options: [["quarter","1/4 truck"],["half","1/2 truck"],["three_quarter","3/4 truck"],["full","Full truck"]],
        initial: "quarter" },
      { key: "heavy_items",   label: "Heavy items (concrete, dirt, etc.)?", type: "bool", initial: false },
    ],
  },
  weed_removal: {
    title: "Weed Removal",
    fields: [
      { key: "area_sqft",     label: "Weedy area (sq ft)", type: "number", min: 50, max: 3000, initial: 300 },
      { key: "difficulty",    label: "Density",             type: "select", options: [["light","Light"],["moderate","Moderate"],["heavy","Heavy"]], initial: "moderate" },
      { key: "hand_pull_beds",label: "Hand-pull in flower beds?", type: "bool", initial: false },
    ],
  },
  rock_installation: {
    title: "Rock Installation",
    fields: [
      { key: "area_sqft",      label: "Area (sq ft)",        type: "number", min: 30, max: 1500, initial: 150 },
      { key: "depth_inches",   label: "Depth (inches)",       type: "number", min: 1, max: 6, initial: 2 },
      { key: "rock_type",      label: "Rock type",            type: "select", options: [["river_rock","River Rock"],["lava_rock","Lava Rock"],["pea_gravel","Pea Gravel"],["decorative","Decorative"]], initial: "river_rock" },
      { key: "include_fabric", label: "Include landscape fabric?", type: "bool", initial: true },
    ],
  },
};

export default function InstantQuoteWizard() {
  const { service } = useParams();
  const navigate = useNavigate();
  const cfg = FIELDS[service];
  const [inputs, setInputs] = useState(() => {
    const init = {};
    (cfg?.fields || []).forEach((f) => { init[f.key] = f.initial; });
    return init;
  });
  const [busy, setBusy] = useState(false);
  const [quote, setQuote] = useState(null);
  const [bookingBusy, setBookingBusy] = useState(false);

  if (!cfg) {
    return (
      <div className="max-w-lg mx-auto p-6 text-center" data-testid="wizard-unknown">
        <div className="text-gray-500 text-sm">Service not found.</div>
        <Link to="/book" className="mt-3 inline-block text-emerald-700 underline">Back to booking</Link>
      </div>
    );
  }

  const getPrice = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/instant-quote/${service}`, { inputs });
      if (data.needs_manual_quote) {
        toast.info(data.reason || "This job needs a custom quote — redirecting...");
        setTimeout(() => navigate(`/quote/request?service=${service}&prefilled=1`), 800);
        return;
      }
      setQuote(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't compute price");
    } finally { setBusy(false); }
  };

  const bookNow = async () => {
    setBookingBusy(true);
    try {
      // Create a pending booking that this instant quote should turn into.
      const { data: booking } = await api.post("/bookings", {
        service_key: service,
        service_name: cfg.title,
        area: "residential",
        yard_area: "both",
        square_feet: inputs.area_sqft || inputs.bed_sqft || 0,
        preferred_date: null,
        preferred_time: "morning",
        payment_method: "stripe",
        save_card_for_future: true,
        price: quote.price,
        instant_quote_inputs: inputs,
        instant_quote_breakdown: quote.breakdown,
      });
      const bookingId = booking.booking_id;
      // Send to Stripe Checkout immediately (Phase 1 requirement).
      const { data: sess } = await api.post("/payments/checkout/session", {
        booking_id: bookingId,
        origin_url: window.location.origin,
        save_card_for_future: true,
      });
      window.location.href = sess.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBookingBusy(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto px-4 sm:px-6 py-6" data-testid={`wizard-${service}`}>
      <Link to="/book" className="inline-flex items-center gap-1 text-xs font-extrabold uppercase tracking-[0.14em] text-emerald-800 hover:underline mb-4">
        <ArrowLeft className="w-3 h-3" /> Back
      </Link>

      <div className="rounded-3xl bg-white border-2 border-emerald-200 shadow-sm overflow-hidden">
        <div className="bg-gradient-to-br from-emerald-50 to-white p-5 border-b border-emerald-100">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 text-[#047857] flex items-center justify-center">
              <Zap className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em] font-extrabold text-emerald-700">Instant Quote</div>
              <div className="text-xl font-extrabold text-gray-900 font-[Outfit]">{cfg.title}</div>
            </div>
          </div>
        </div>

        {!quote ? (
          <div className="p-5 space-y-4">
            {cfg.fields.map((f) => (
              <FieldRow key={f.key} field={f} value={inputs[f.key]}
                        onChange={(v) => setInputs((prev) => ({ ...prev, [f.key]: v }))} />
            ))}
            <Button
              onClick={getPrice}
              disabled={busy}
              className="w-full h-12 rounded-full bg-[#047857] hover:bg-[#065F46] text-white font-extrabold shadow-lg"
              data-testid="get-price-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
              Get Instant Price
            </Button>
            <Link
              to={`/quote/request?service=${service}`}
              className="block text-center text-xs text-gray-500 hover:text-emerald-700"
              data-testid="switch-to-request"
            >
              Complex job? <span className="underline">Request a custom quote instead →</span>
            </Link>
          </div>
        ) : (
          <div className="p-5 space-y-4" data-testid="wizard-price-panel">
            <div className="text-center">
              <div className="text-[10px] uppercase tracking-[0.16em] font-extrabold text-emerald-800">Your instant price</div>
              <div className="text-5xl font-extrabold text-[#047857] font-[Outfit] mt-1" data-testid="wizard-price">
                ${quote.price}
              </div>
            </div>
            <div className="rounded-2xl bg-gray-50 border border-gray-200 p-3 space-y-1.5 text-sm">
              {quote.breakdown.map((row, i) => (
                <div key={i} className="flex items-center justify-between text-gray-700">
                  <span className="truncate mr-2">{row.label}</span>
                  <span className="font-bold flex-shrink-0">${Number(row.amount).toFixed(2)}</span>
                </div>
              ))}
            </div>
            <Button
              onClick={bookNow}
              disabled={bookingBusy}
              className="w-full h-13 rounded-full bg-gradient-to-br from-[#10B981] to-[#047857] text-white font-extrabold text-base shadow-[0_8px_24px_rgba(4,120,87,0.28)]"
              data-testid="book-now-btn"
            >
              {bookingBusy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-5 h-5 mr-2" />}
              Book Now · ${quote.price}
            </Button>
            <button
              onClick={() => setQuote(null)}
              className="w-full text-xs text-gray-500 hover:text-emerald-700 py-1"
              data-testid="edit-inputs-btn"
            >
              Edit answers →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function FieldRow({ field, value, onChange }) {
  if (field.type === "number") {
    return (
      <label className="block">
        <span className="text-xs uppercase tracking-[0.14em] font-extrabold text-gray-600">{field.label}</span>
        <input
          type="number"
          value={value ?? ""}
          min={field.min} max={field.max} step={field.step ?? 1}
          onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
          className="mt-1 w-full h-12 px-3 rounded-xl border-2 border-gray-200 focus:border-emerald-400 outline-none text-base"
          data-testid={`input-${field.key}`}
        />
      </label>
    );
  }
  if (field.type === "select") {
    return (
      <label className="block">
        <span className="text-xs uppercase tracking-[0.14em] font-extrabold text-gray-600">{field.label}</span>
        <select
          value={value || field.initial}
          onChange={(e) => onChange(e.target.value)}
          className="mt-1 w-full h-12 px-3 rounded-xl border-2 border-gray-200 focus:border-emerald-400 outline-none text-base bg-white"
          data-testid={`input-${field.key}`}
        >
          {field.options.map(([v, l]) => (<option key={v} value={v}>{l}</option>))}
        </select>
      </label>
    );
  }
  // bool
  return (
    <label className="flex items-center gap-2 p-3 rounded-xl border-2 border-gray-100 cursor-pointer hover:border-emerald-200">
      <input
        type="checkbox"
        checked={!!value}
        onChange={(e) => onChange(e.target.checked)}
        className="w-5 h-5 accent-[#047857]"
        data-testid={`input-${field.key}`}
      />
      <span className="text-sm font-bold text-gray-800">{field.label}</span>
    </label>
  );
}
